import {
  Box,
  Typography,
  TextField,
  Chip,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
  Checkbox,
  Link,
} from "@mui/material";
import {
  OpenInNew,
  ArrowForward,
  LocalOfferOutlined,
  CheckCircleOutline,
  FavoriteBorder,
  BoltOutlined,
  PeopleAltOutlined,
  StorefrontOutlined,
  LockOutlined,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {RTL} from "../cache/CacheRtl";
import {
  getMarketplacePartnerships,
  getMarketplacePreview,
  submitMarketplaceLead,
  getMarketplaceStatus,
} from "../services/PartnershipService";
import {CATEGORY_OPTIONS, BADGE_META} from "../componenets/partnerships/PartnershipDialog";
import PhoneInput from "../componenets/shared/PhoneInput";
import {useMessageContext} from "../contexts/MessageContext";
import {useAuth} from "../contexts/AuthContext";

const categoryLabel = (value) =>
  CATEGORY_OPTIONS.find((c) => c.value === value)?.label || value;

// Per-category dark cover color for partner cards (the DTO carries no color field).
const COVER_COLORS = {
  NUTRITION: "#16243B",
  EQUIPMENT: "#1C1917",
  SOFTWARE: "#2A1A4A",
  HEALTH: "#0F2A43",
  EDUCATION: "#3A1F0E",
  WELLNESS: "#10302A",
  OTHER: "#1A1A2E",
};
const coverColor = (category) => COVER_COLORS[category] || "#1A1A2E";

// Per-partner custom color wins; falls back to the per-category default.
const partnerCoverColor = (partner) =>
  partner?.backgroundColor || coverColor(partner?.category);

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase() || "?";

const Marketplace = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({email: "", name: "", phone: "", message: ""});
  const [terms, setTerms] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState({open: false, canManage: false});
  const [statusLoaded, setStatusLoaded] = useState(false);

  const {notificationHandler} = useMessageContext();
  const {currentUser, isSuperAdmin} = useAuth();
  const navigate = useNavigate();

  const setField = (field, value) => {
    setForm((prev) => ({...prev, [field]: value}));
    if (field === "email" && emailError) setEmailError("");
  };

  const load = useCallback(() => {
    setLoading(true);
    // Super-admin previews the marketplace via /system/partnership/marketplace-preview (returns the
    // consumer cards regardless of open/closed). Company users get the live coach-facing listing.
    const fetchPartners = isSuperAdmin() ? getMarketplacePreview() : getMarketplacePartnerships();
    fetchPartners
      .then((res) => setPartners(res.data || []))
      .catch((e) => {
        console.error("Error loading marketplace:", e);
        notificationHandler.error("טעינת המרקטפלייס נכשלה");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // The open/closed status gate is a company-user concern (admin/marketplace/status is
    // ADMIN/TRAINER-scoped). For a super-admin the preview always renders, so treat it as open.
    if (isSuperAdmin()) {
      setStatus({open: true, canManage: true});
      setStatusLoaded(true);
      return;
    }
    getMarketplaceStatus()
      .then((res) => setStatus(res.data || {open: false, canManage: false}))
      .catch((e) => console.error("Error loading marketplace status:", e))
      .finally(() => setStatusLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const categoriesPresent = useMemo(() => {
    const set = new Set(partners.map((p) => p.category));
    return CATEGORY_OPTIONS.filter((c) => set.has(c.value));
  }, [partners]);

  const filtered = useMemo(() => {
    return partners.filter((p) => {
      const catMatch = category === "all" || p.category === category;
      const term = keyword.trim().toLowerCase();
      const searchMatch =
        !term ||
        p.name?.toLowerCase().includes(term) ||
        p.tagline?.toLowerCase().includes(term);
      return catMatch && searchMatch;
    });
  }, [partners, category, keyword]);

  const openDetail = (partner) => {
    setSelected(partner);
    // Pre-fill from the logged-in coach; fields stay editable.
    setForm({
      email: currentUser?.email || "",
      name: currentUser?.name || "",
      phone: currentUser?.phoneNumber || "",
      message: "",
    });
    setTerms(true);
    setEmailError("");
    setSubmitted(false);
  };

  const goBack = () => {
    setSelected(null);
    setSubmitted(false);
  };

  const handleSubmitLead = async () => {
    const email = form.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("יש להזין כתובת אימייל תקינה");
      return;
    }
    setSubmitting(true);
    try {
      await submitMarketplaceLead(selected.id, {
        email,
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      setSubmitted(true);
    } catch (e) {
      console.error("Error submitting lead:", e);
      notificationHandler.error("שליחת הפנייה נכשלה, נסה שוב מאוחר יותר");
    } finally {
      setSubmitting(false);
    }
  };

  const openExternal = (url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // ── Detail view ──────────────────────────────────────────────────────────
  if (selected) {
    return (
      <RTL>
        <Box>
          <Button startIcon={<ArrowForward />} onClick={goBack} sx={{mb: 3, color: "#6B7280"}}>
            חזרה למרקטפלייס
          </Button>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {xs: "1fr", md: "1fr 380px"},
              gap: 3,
              alignItems: "start",
            }}
          >
            {/* Info panel */}
            <Box sx={{minWidth: 0}}>
              <Card variant="outlined" sx={{borderRadius: "12px", overflow: "hidden"}}>
                <Box
                  sx={{
                    position: "relative",
                    height: 160,
                    bgcolor: partnerCoverColor(selected),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {selected.badge && BADGE_META[selected.badge] && (
                    <Box
                      component="span"
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "4px 10px",
                        borderRadius: "10px",
                        letterSpacing: ".3px",
                        bgcolor: BADGE_META[selected.badge].bg,
                        color: BADGE_META[selected.badge].color,
                      }}
                    >
                      {BADGE_META[selected.badge].label}
                    </Box>
                  )}
                  {selected.logoUrl ? (
                    <Box
                      component="img"
                      src={selected.logoUrl}
                      alt={selected.name}
                      sx={{maxHeight: 80, maxWidth: "70%", objectFit: "contain"}}
                    />
                  ) : (
                    <Typography sx={{fontSize: 42, fontWeight: 800, color: "#fff", letterSpacing: "-2px"}}>
                      {initials(selected.name)}
                    </Typography>
                  )}
                </Box>
                <CardContent sx={{p: 3}}>
                  <Typography variant="caption" sx={{color: "#9CA3AF", fontWeight: 600}}>
                    {categoryLabel(selected.category)}
                  </Typography>
                  <Typography variant="h5" sx={{fontWeight: 800, mb: 1}}>
                    {selected.name}
                  </Typography>
                  {selected.tagline && (
                    <Typography sx={{color: "#6B7280", mb: 2}}>{selected.tagline}</Typography>
                  )}
                  {selected.websiteUrl && (
                    <Button
                      size="small"
                      endIcon={<OpenInNew />}
                      onClick={() => openExternal(selected.websiteUrl)}
                      sx={{px: 0, mb: 2}}
                    >
                      לאתר השותף
                    </Button>
                  )}

                  {selected.offer && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        bgcolor: "rgba(7,187,166,0.12)",
                        border: "1px solid rgba(7,187,166,0.2)",
                        borderRadius: "8px",
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <LocalOfferOutlined sx={{color: "primary.main"}} />
                      <Typography sx={{fontWeight: 700}}>{selected.offer}</Typography>
                    </Box>
                  )}

                  {selected.description && (
                    <>
                      <Typography variant="overline" sx={{color: "#9CA3AF"}}>
                        תיאור השירות
                      </Typography>
                      <Typography sx={{color: "#6B7280", lineHeight: 1.7, mb: 2}}>
                        {selected.description}
                      </Typography>
                    </>
                  )}

                  {selected.terms && (
                    <>
                      <Divider sx={{my: 2}} />
                      <Typography variant="overline" sx={{color: "#9CA3AF"}}>
                        תנאי המבצע
                      </Typography>
                      <Typography sx={{color: "#6B7280", lineHeight: 1.7}}>
                        {selected.terms}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Contact / deal panel */}
            <Box sx={{minWidth: 0, position: {md: "sticky"}, top: {md: 0}}}>
              <Card variant="outlined" sx={{borderRadius: "12px", p: 3}}>
                {submitted ? (
                  <Box sx={{textAlign: "center", py: 3}}>
                    <CheckCircleOutline sx={{fontSize: 52, color: "primary.main", mb: 1}} />
                    <Typography variant="h6" sx={{fontWeight: 700}}>
                      הפנייה נשלחה!
                    </Typography>
                    <Typography sx={{color: "#6B7280", mt: 1}}>
                      ניצור עם {selected.name} קשר בהקדם.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {selected.dealUrl && (
                      <>
                        <Button
                          fullWidth
                          variant="contained"
                          endIcon={<OpenInNew />}
                          onClick={() => openExternal(selected.dealUrl)}
                          sx={{height: 44, mb: 2}}
                        >
                          קבל את המבצע שלך
                        </Button>
                        <Divider sx={{my: 2}}>
                          <Typography variant="caption" sx={{color: "#9CA3AF"}}>
                            או השאר פרטים לקבלת מידע
                          </Typography>
                        </Divider>
                      </>
                    )}

                    <Typography variant="h6" sx={{fontWeight: 700, mb: 0.5}}>
                      מעוניינים לשמוע עוד?
                    </Typography>
                    <Typography sx={{color: "#6B7280", fontSize: 13, mb: 2.5}}>
                      מלאו את הפרטים ונציג יחזור אליכם.
                    </Typography>

                    <Box sx={{mb: 2}}>
                      <Typography sx={{fontSize: 12.5, fontWeight: 600, mb: 0.75}}>
                        אימייל <Box component="span" sx={{color: "primary.main"}}>*</Box>
                      </Typography>
                      <TextField
                        type="email"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        error={!!emailError}
                        helperText={emailError}
                        placeholder="coach@example.com"
                        fullWidth
                        size="small"
                      />
                    </Box>

                    <Box sx={{mb: 2}}>
                      <Typography sx={{fontSize: 12.5, fontWeight: 600, mb: 0.75}}>שם מלא</Typography>
                      <TextField
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="ישראל ישראלי"
                        fullWidth
                        size="small"
                      />
                    </Box>

                    <Box sx={{mb: 2}}>
                      <Typography sx={{fontSize: 12.5, fontWeight: 600, mb: 0.75}}>מספר טלפון</Typography>
                      <PhoneInput
                        value={form.phone}
                        onChange={(value) => setField("phone", value)}
                      />
                    </Box>

                    <Box sx={{display: "flex", alignItems: "center", mb: 2}}>
                      <Checkbox
                        checked={terms}
                        onChange={(e) => setTerms(e.target.checked)}
                        size="small"
                        sx={{p: 0.5}}
                      />
                      <Typography sx={{fontSize: 12.5, color: "#6B7280"}}>
                        אני מאשר/ת את{" "}
                        <Link href="#" underline="hover" sx={{color: "primary.main", fontWeight: 600}}>
                          תנאי השימוש
                        </Link>
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSubmitLead}
                      disabled={submitting || !terms}
                      sx={{height: 44}}
                    >
                      {submitting ? "שולח..." : "שלח פנייה"}
                    </Button>
                  </>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </RTL>
    );
  }

  // ── Wait for the open/closed status before choosing a view ────────────────
  if (!statusLoaded) {
    return (
      <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Closed for non-managers (trainers) ────────────────────────────────────
  if (!status.open && !status.canManage) {
    return (
      <RTL>
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 360, textAlign: "center", color: "#9CA3AF", gap: 1.5}}>
          <StorefrontOutlined sx={{fontSize: 56, opacity: 0.5}} />
          <Typography sx={{fontSize: 18, fontWeight: 700, color: "#6B7280"}}>
            המרקטפלייס יהיה זמין בקרוב
          </Typography>
          <Typography sx={{fontSize: 14}}>חזרו לבדוק בהמשך — אנחנו מוסיפים שותפים והטבות.</Typography>
        </Box>
      </RTL>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────
  return (
    <RTL>
      <Box>
        {/* Closed banner — shown to managers (SYSTEM_ADMIN) while the marketplace is off */}
        {!status.open && status.canManage && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              bgcolor: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: "12px",
              p: "12px 16px",
              mb: 3,
            }}
          >
            <LockOutlined sx={{color: "#DC2626", fontSize: 20}} />
            <Box>
              <Typography sx={{fontWeight: 700, color: "#991B1B", fontSize: 13.5}}>
                המרקטפלייס סגור
              </Typography>
              <Typography sx={{color: "#B91C1C", fontSize: 12.5}}>
                כרגע רק אתה רואה אותו.{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate("/app/partnerships")}
                  sx={{color: "#991B1B", fontWeight: 700, textDecorationColor: "#991B1B", verticalAlign: "baseline"}}
                >
                  פתח אותו ממסך ניהול השותפים
                </Link>{" "}
                כדי להציג אותו למאמנים.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Hero */}
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            p: {md: "32px 36px", xs: "20px"},
            mb: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: {xs: "column", md: "row"},
            gap: 4,
          }}
        >
          {/* Text (visually right in RTL) */}
          <Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                bgcolor: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: "20px",
                px: 1.25,
                py: 0.5,
                mb: 1.5,
              }}
            >
              <PeopleAltOutlined sx={{color: "#6B7280", fontSize: 14}} />
              <Typography variant="caption" sx={{fontWeight: 600, color: "#6B7280"}}>
                שיתופי פעולה בלעדיים
              </Typography>
            </Box>
            <Typography variant="h4" sx={{fontWeight: 800, mb: 1, lineHeight: 1.2}}>
              פרטנרים שנבחרו
              <br />
              במיוחד עבורך
            </Typography>
            <Typography sx={{color: "#6B7280", maxWidth: 420, lineHeight: 1.6}}>
              מותגים ומומחים מובילים שמרחיבים את השירות שאתה נותן — עם הטבות בלעדיות שלא
              תמצא בשום מקום אחר.
            </Typography>
          </Box>

          {/* Feature pills (visually left in RTL) */}
          <Box sx={{display: "flex", flexDirection: "column", gap: 1.25, flexShrink: 0}}>
            {[
              {
                icon: <FavoriteBorder sx={{fontSize: 15, color: "primary.main"}} />,
                title: "הטבות בלעדיות",
                sub: "מחירים שלא זמינים לציבור",
              },
              {
                icon: <BoltOutlined sx={{fontSize: 15, color: "primary.main"}} />,
                title: "אינטגרציה ישירה",
                sub: "מחובר לפרופיל הלקוח",
              },
              {
                icon: <PeopleAltOutlined sx={{fontSize: 15, color: "primary.main"}} />,
                title: "נבחרו בקפידה",
                sub: "פרטנרים מוכחים בלבד",
              },
            ].map((pill) => (
              <Box
                key={pill.title}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  p: "12px 16px",
                  minWidth: {md: 230},
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: "rgba(7,187,166,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {pill.icon}
                </Box>
                <Box>
                  <Typography sx={{fontSize: 13, fontWeight: 700}}>{pill.title}</Typography>
                  <Typography sx={{fontSize: 11.5, color: "#9CA3AF"}}>{pill.sub}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center"}}>
          <TextField
            size="small"
            placeholder="חיפוש שותף..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            sx={{minWidth: 260}}
          />
          <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
            <Chip
              label="הכל"
              onClick={() => setCategory("all")}
              color={category === "all" ? "primary" : "default"}
              variant={category === "all" ? "filled" : "outlined"}
            />
            {categoriesPresent.map((c) => (
              <Chip
                key={c.value}
                label={c.label}
                onClick={() => setCategory(c.value)}
                color={category === c.value ? "primary" : "default"}
                variant={category === c.value ? "filled" : "outlined"}
              />
            ))}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{textAlign: "center", py: 8, color: "#9CA3AF"}}>
            <Typography>לא נמצאו שותפים פעילים עבור החיפוש</Typography>
          </Box>
        ) : (
          <>
            {/* Section count */}
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#9CA3AF",
                textTransform: "uppercase",
                letterSpacing: ".04em",
                mb: 1.75,
              }}
            >
              {filtered.length} שיתופי פעולה זמינים
            </Typography>

            <Box
              sx={{
                display: "grid",
                // Match Arbox marketplace sizing: large cards, 3 across at desktop,
                // dropping to 2 / 1 on narrower widths.
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 3,
              }}
            >
              {filtered.map((p) => (
                  <Card
                    key={p.id}
                    variant="outlined"
                    onClick={() => openDetail(p)}
                    sx={{
                      borderRadius: "12px",
                      border: "1px solid #E5E7EB",
                      cursor: "pointer",
                      height: "100%",
                      overflow: "hidden",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "rgba(7,187,166,0.3)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    {/* Cover — large square logo panel, matching Arbox sizing */}
                    <Box
                      sx={{
                        position: "relative",
                        height: 200,
                        bgcolor: partnerCoverColor(p),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {p.badge && BADGE_META[p.badge] && (
                        <Box
                          component="span"
                          sx={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "3px 8px",
                            borderRadius: "10px",
                            letterSpacing: ".3px",
                            bgcolor: BADGE_META[p.badge].bg,
                            color: BADGE_META[p.badge].color,
                          }}
                        >
                          {BADGE_META[p.badge].label}
                        </Box>
                      )}
                      {p.logoUrl ? (
                        <Box
                          component="img"
                          src={p.logoUrl}
                          alt={p.name}
                          sx={{maxHeight: 160, maxWidth: "88%", objectFit: "contain"}}
                        />
                      ) : (
                        <Typography
                          sx={{fontSize: 48, fontWeight: 800, color: "#fff", letterSpacing: "-1px"}}
                        >
                          {initials(p.name)}
                        </Typography>
                      )}
                    </Box>

                    <CardContent sx={{p: "20px", "&:last-child": {pb: "24px"}}}>
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 600,
                          color: "#9CA3AF",
                          textTransform: "uppercase",
                          letterSpacing: ".04em",
                          mb: 0.5,
                        }}
                      >
                        {categoryLabel(p.category)}
                      </Typography>
                      <Typography sx={{fontWeight: 700, fontSize: 17, mb: 0.75}}>{p.name}</Typography>
                      <Typography
                        sx={{
                          color: "#6B7280",
                          fontSize: 13.5,
                          lineHeight: 1.5,
                          mb: 1.75,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {p.tagline}
                      </Typography>

                      {/* Footer */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          pt: 1.5,
                          borderTop: "1px solid #F3F4F6",
                        }}
                      >
                        <Typography sx={{fontWeight: 700, color: "primary.main", fontSize: 12.5}}>
                          {p.offer || ""}
                        </Typography>
                        {p.dealUrl && (
                          <Button
                            size="small"
                            endIcon={<OpenInNew sx={{fontSize: "11px !important"}} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openExternal(p.dealUrl);
                            }}
                            sx={{
                              flexShrink: 0,
                              bgcolor: "rgba(7,187,166,0.12)",
                              color: "primary.main",
                              border: "1px solid rgba(7,187,166,0.2)",
                              borderRadius: "8px",
                              fontSize: 12,
                              fontWeight: 700,
                              px: 1.25,
                              "&:hover": {
                                bgcolor: "primary.main",
                                color: "#fff",
                                borderColor: "primary.main",
                              },
                            }}
                          >
                            קבל הטבה
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
              ))}
            </Box>
          </>
        )}
      </Box>
    </RTL>
  );
};

export default Marketplace;
